// M+3.6 C2 — Admin theme editor with live preview, contrast audit,
// slot pinning, custom push. Admin-gated by the parent SettingsView.
//
// Edit lifecycle: clicking Edit on a theme card copies its vars into a
// local buffer; preview + audit run against the buffer. Save writes the
// buffer back to the API; Cancel discards. The buffer reflects every key in
// EDITABLE_VARS; the API allowlist (themes.js ALLOWED_VAR_KEYS) must stay in
// sync with that list or saved values for the missing keys are dropped.

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Trash2,
  X,
  Plus,
  Sun,
  Moon,
  Palette,
  Pencil,
  Check,
  AlertTriangle,
  Download,
  Upload,
} from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import {
  EDITABLE_VARS,
  DERIVED_VARS,
  OVERRIDABLE_DERIVED_VARS,
  varGroups,
  varLabel,
  varSource, // 134 §4(a)
  varDefault,
  deriveThemeVars,
  auditContrast,
  contrastRatio,
} from "../lib/themeDerivation";
import ColorOrVarPicker from "./ColorOrVarPicker";
import MenuSelect from "./MenuSelect"; // 154B §2
import { CT, RAD, FS } from "../lib/tokens";

// "31/10/2026" from a Date or ISO string.
function fmtBxlDate(input: any) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

// "yyyy-mm-dd" → ISO instant for end-of-day in Europe/Brussels.
// Computes the Brussels offset for that exact moment so DST switchovers
// resolve correctly (Mar/Oct).
function endOfDayBrusselsISO(yyyymmdd: any) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return null;
  const naive = new Date(`${yyyymmdd}T23:59:59Z`);
  const bxlStr = naive.toLocaleString("en-US", {
    timeZone: "Europe/Brussels",
    hourCycle: "h23",
  });
  const utcStr = naive.toLocaleString("en-US", {
    timeZone: "UTC",
    hourCycle: "h23",
  });
  const offsetMs = Date.parse(bxlStr) - Date.parse(utcStr);
  return new Date(naive.getTime() - offsetMs).toISOString();
}

// ISO → "yyyy-mm-dd" in Brussels TZ for the date input value.
function isoToBxlDateInput(iso: any) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Brussels",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")!.value;
  const m = parts.find((p) => p.type === "month")!.value;
  const day = parts.find((p) => p.type === "day")!.value;
  return `${y}-${m}-${day}`;
}

const SlotIcon = ({ slot, size = 14 }: { slot?: any; size?: number }) => {
  if (slot === "day") return <Sun size={size} />;
  if (slot === "night") return <Moon size={size} />;
  return <Palette size={size} />;
};

// Compact preview of a theme. Renders representative UI elements:
// header / paragraph / muted line / button row / status pills / tag chip.
function LivePreview({ vars }: { vars?: any }) {
  const { t } = useTranslation();
  const v = useMemo(() => deriveThemeVars(vars || {}), [vars]);
  const wrap = {
    background: v["--bd"],
    color: v["--tp"],
    border: "1px solid " + v["--br"],
    borderRadius: RAD.r8,
    padding: 12,
    marginTop: 8,
    fontFamily: "inherit",
  };
  const card = {
    background: v["--bs"],
    borderRadius: RAD.r6,
    padding: 10,
    marginBottom: 8,
  };
  const pill = (fg: any, bg: any, label: any) => (
    <span
      style={{
        background: bg,
        color: fg,
        fontSize: FS.s10,
        padding: "2px 8px",
        borderRadius: RAD.r10,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
  return (
    <div style={wrap}>
      <div style={card}>
        <h4 style={{ margin: 0, fontSize: FS.s13, color: v["--tp"] }}>
          {t("theme.preview.sample_case_row", { fallback: "Sample case row" })}
        </h4>
        <p style={{ fontSize: FS.s11, color: v["--ts"], margin: "4px 0" }}>
          {t("theme.preview.body_copy", {
            fallback: "Body copy uses --ts; primary stays --tp.",
          })}
        </p>
        <p style={{ fontSize: FS.s10_5, color: v["--tm"], margin: 0 }}>
          {t("theme.preview.muted_detail", {
            fallback: "Muted detail line (--tm derived).",
          })}
        </p>
      </div>
      <div
        style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}
      >
        <button
          style={{
            background: v["--as"],
            color: v["--ac"],
            border: "none",
            borderRadius: RAD.r6,
            padding: "4px 10px",
            fontSize: FS.s11,
            cursor: "pointer",
          }}
        >
          {t("theme.preview.btn_primary", { fallback: "Primary" })}
        </button>
        <button
          style={{
            background: "transparent",
            color: v["--ts"],
            border: "1px solid " + v["--br"],
            borderRadius: RAD.r6,
            padding: "4px 10px",
            fontSize: FS.s11,
            cursor: "pointer",
          }}
        >
          {t("theme.preview.btn_ghost", { fallback: "Ghost" })}
        </button>
        <button
          style={{
            background: v["--rs"],
            color: v["--rd"],
            border: "none",
            borderRadius: RAD.r6,
            padding: "4px 10px",
            fontSize: FS.s11,
            cursor: "pointer",
          }}
        >
          {t("theme.preview.btn_danger", { fallback: "Danger" })}
        </button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {pill(
          v["--gn"],
          v["--gs"],
          t("theme.preview.pill_paid", { fallback: "Paid" }),
        )}
        {pill(
          v["--am"],
          v["--ams"],
          t("theme.preview.pill_pending", { fallback: "Pending" }),
        )}
        {pill(
          v["--rd"],
          v["--rs"],
          t("theme.preview.pill_overdue", { fallback: "Overdue" }),
        )}
        {pill(
          v["--pp"],
          v["--ps"],
          t("theme.preview.pill_ready", { fallback: "Ready" }),
        )}
      </div>
    </div>
  );
}

// Phase 44 — scoped-container theme preview. LivePreview (above) resolves
// deriveThemeVars() to hardcoded hex per element, so it can only ever show the
// fixed handful it hardcodes. This instead applies the draft's derived vars to
// a CONTAINER element via setProperty — scoped to that subtree, NOT
// document.documentElement — so the sample elements below consume the real
// var(--XX) tokens and resolve to the draft, while the rest of the app (the
// picker chrome included) stays on the live theme. Extending coverage means
// adding real var()-consuming markup, not another hardcoded colour.
//
// Coverage honesty (item 5): tokens with real UI to model get an authentic
// sample; tokens with a single narrow consumer get that consumer, labelled
// with where it lands (--cb/--bf initials badge, --or archive icon, --gnm/--rdm
// library borrow dots); --ct has zero live consumers anywhere and is a labelled
// swatch only. DERIVED_VARS are live read-only swatches — they follow their
// parent bases via deriveThemeVars and are never picked here.
function ScopedThemePreview({ vars }: { vars?: any }) {
  const { t } = useTranslation();
  const ref = useRef<any>(null);
  // Build a COMPLETE editable base (defaults for any key the sparse draft
  // omits) then derive, so all 26 vars are set on the container and no token
  // leaks through to the live :root theme.
  const full = useMemo(() => {
    const base: Record<string, any> = {};
    EDITABLE_VARS.forEach((k) => {
      base[k] = (vars && vars[k]) || varDefault(k);
    });
    return deriveThemeVars(base);
  }, [vars]);
  // Apply onto the container element only. useLayoutEffect runs before paint,
  // so the sample never flashes the live theme first.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    Object.entries(full).forEach(([k, v]) => el.style.setProperty(k, v));
    return () => {
      Object.keys(full).forEach((k) => el.style.removeProperty(k));
    };
  }, [full]);

  const secLbl = (txt: any) => (
    <div
      style={{
        fontSize: FS.s10,
        fontWeight: 600,
        color: "var(--tm)",
        textTransform: "uppercase",
        letterSpacing: 0.4,
        margin: "10px 0 4px",
      }}
    >
      {txt}
    </div>
  );
  const cap = {
    fontSize: FS.s8_5,
    color: "var(--tm)",
    fontFamily: "'JetBrains Mono',monospace",
    marginTop: 2,
  };
  const pill = (bg: any, fg: any, label: any) => (
    <span
      style={{
        background: bg,
        color: fg,
        borderRadius: RAD.r10,
        padding: "2px 8px",
        fontSize: FS.s10,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );

  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: FS.s10,
          fontWeight: 600,
          color: "var(--tm)",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 4,
        }}
      >
        {t("theme.preview.title", {
          fallback: "Live preview — draft applied to this panel only",
        })}
      </div>
      {/* Scoped container: draft vars live here and inherit to the subtree. */}
      <div
        ref={ref}
        style={{
          background: "var(--bd)",
          color: "var(--tp)",
          border: "1px solid var(--br)",
          borderRadius: RAD.r8,
          padding: 10,
          fontFamily: "inherit",
        }}
      >
        {/* App shell: sidebar (--bs) + active/normal nav (--ac/--bc/--ts) + card */}
        <div style={{ display: "flex", gap: 8 }}>
          <div
            style={{
              width: 104,
              background: "var(--bs)",
              border: "1px solid var(--br)",
              borderRadius: RAD.r6,
              padding: 6,
            }}
          >
            <div
              style={{
                color: "var(--ac)",
                fontWeight: 700,
                fontSize: FS.s11,
                marginBottom: 6,
              }}
            >
              {t("field.firm", { fallback: "Firm" })}
            </div>
            <div
              style={{
                background: "var(--bc)",
                color: "var(--ac)",
                borderRadius: RAD.r5,
                padding: "3px 6px",
                fontSize: FS.s10,
                fontWeight: 600,
              }}
            >
              {t("nav.cases", { fallback: "Cases" })}
            </div>
            <div
              style={{ color: "var(--ts)", padding: "3px 6px", fontSize: FS.s10 }}
            >
              {t("nav.agenda", { fallback: "Agenda" })}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                background: "var(--bc)",
                border: "1px solid var(--br)",
                borderRadius: RAD.r6,
                padding: 8,
              }}
            >
              <div
                style={{ color: "var(--tp)", fontWeight: 600, fontSize: FS.s12 }}
              >
                {t("theme.preview.sample_parties", { fallback: "Dupont / Acme —" })}{" "}
                {t("theme.preview.dispute", { fallback: "dispute" })}
              </div>
              <div
                style={{
                  color: "var(--ts)",
                  fontSize: FS.s11,
                  margin: "3px 0",
                }}
              >
                {t("theme.preview.secondary_text_line", {
                  fallback: "Secondary text (--ts) rides body copy.",
                })}
              </div>
              <div style={{ color: "var(--tm)", fontSize: FS.s10_5 }}>
                {t("theme.preview.muted_meta_line", {
                  fallback: 'Muted meta line (--tm) — "updated 2d ago".',
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Table: header (--bs), normal row (--tp), hover row (--bh) */}
        {secLbl(t("theme.preview.sec_table", { fallback: "Table" }))}
        <div
          style={{
            border: "1px solid var(--br)",
            borderRadius: RAD.r6,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              background: "var(--bs)",
              color: "var(--tm)",
              fontSize: FS.s10,
              fontWeight: 600,
              padding: "4px 8px",
            }}
          >
            <span style={{ flex: 1 }}>
              {t("te.doubles.matter", { fallback: "Matter" })}
            </span>
            <span>{t("common.status", { fallback: "Status" })}</span>
          </div>
          <div
            style={{
              display: "flex",
              color: "var(--tp)",
              fontSize: FS.s11,
              padding: "4px 8px",
              borderTop: "1px solid var(--br)",
            }}
          >
            <span style={{ flex: 1 }}>
              {t("theme.preview.normal_row", { fallback: "Normal row" })}
            </span>
            <span style={{ color: "var(--tm)" }}>—</span>
          </div>
          <div
            style={{
              display: "flex",
              background: "var(--bh)",
              color: "var(--tp)",
              fontSize: FS.s11,
              padding: "4px 8px",
              borderTop: "1px solid var(--br)",
            }}
          >
            <span style={{ flex: 1 }}>
              {t("theme.preview.hover_row", { fallback: "Hover row (--bh)" })}
            </span>
            <span style={{ color: "var(--tm)" }}>—</span>
          </div>
        </div>

        {/* Inputs: default (--bi) + focused (--ac ring via --ag) */}
        {secLbl(t("theme.preview.sec_inputs", { fallback: "Inputs" }))}
        <div style={{ display: "flex", gap: 6 }}>
          <div
            style={{
              flex: 1,
              background: "var(--bi)",
              border: "1px solid var(--br)",
              color: "var(--tp)",
              borderRadius: RAD.r5,
              padding: "4px 8px",
              fontSize: FS.s11,
            }}
          >
            {t("theme.preview.input_default", { fallback: "Input (--bi)" })}
          </div>
          <div
            style={{
              flex: 1,
              background: "var(--bi)",
              border: "1px solid var(--ac)",
              boxShadow: "0 0 0 2px var(--ag)",
              color: "var(--tp)",
              borderRadius: RAD.r5,
              padding: "4px 8px",
              fontSize: FS.s11,
            }}
          >
            {t("theme.preview.input_focused", { fallback: "Focused (--ac)" })}
          </div>
        </div>

        {/* Buttons: primary (--as/--ac), ghost (--br/--ts), danger (--rs/--rd) */}
        {secLbl(t("theme.preview.sec_buttons", { fallback: "Buttons" }))}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span
            style={{
              background: "var(--as)",
              color: "var(--ac)",
              borderRadius: RAD.r6,
              padding: "4px 10px",
              fontSize: FS.s11,
              fontWeight: 600,
            }}
          >
            {t("theme.preview.btn_primary", { fallback: "Primary" })}
          </span>
          <span
            style={{
              border: "1px solid var(--br)",
              color: "var(--ts)",
              borderRadius: RAD.r6,
              padding: "4px 10px",
              fontSize: FS.s11,
            }}
          >
            {t("theme.preview.btn_ghost", { fallback: "Ghost" })}
          </span>
          <span
            style={{
              background: "var(--rs)",
              color: "var(--rd)",
              borderRadius: RAD.r6,
              padding: "4px 10px",
              fontSize: FS.s11,
              fontWeight: 600,
            }}
          >
            {t("theme.preview.btn_danger", { fallback: "Danger" })}
          </span>
        </div>

        {/* Status pills — status colour on its derived soft bg */}
        {secLbl(t("theme.preview.sec_status_pills", { fallback: "Status pills" }))}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {pill(
            "var(--gs)",
            "var(--gn)",
            t("theme.preview.pill_paid", { fallback: "Paid" }),
          )}
          {pill(
            "var(--ams)",
            "var(--am)",
            t("theme.preview.pill_pending", { fallback: "Pending" }),
          )}
          {pill(
            "var(--rs)",
            "var(--rd)",
            t("theme.preview.pill_overdue", { fallback: "Overdue" }),
          )}
          {pill(
            "var(--ps)",
            "var(--pp)",
            t("theme.preview.pill_ready", { fallback: "Ready" }),
          )}
        </div>

        {/* Narrow / single-site tokens — real consumers, labelled where they land */}
        {secLbl(t("theme.preview.sec_badges", { fallback: "Badges & single-site tokens" }))}
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: RAD.pill,
                background: "var(--cb)",
                color: "var(--bf)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: FS.s10,
                fontWeight: 700,
                margin: "0 auto",
              }}
            >
              {t("theme.preview.badge_initials", { fallback: "JD" })}
            </div>
            <div style={cap}>
              {t("theme.preview.cap_initials_badge", {
                fallback: "--cb/--bf · initials badge",
              })}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{ color: "var(--or)", fontSize: FS.s18, lineHeight: "26px" }}
            >
              ▣
            </div>
            <div style={cap}>
              {t("theme.preview.cap_archive_icon", {
                fallback: "--or · archive/unlock icon",
              })}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                display: "flex",
                gap: 4,
                justifyContent: "center",
                height: 26,
                alignItems: "center",
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: RAD.pill,
                  background: "var(--gnm)",
                }}
              />
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: RAD.pill,
                  background: "var(--rdm)",
                }}
              />
            </div>
            <div style={cap}>
              {t("theme.preview.cap_library_state", {
                fallback: "--gnm/--rdm · library borrow state",
              })}
            </div>
          </div>
        </div>

        {/* Derived tokens — live read-only swatches (follow their bases) */}
        {secLbl(
          t("theme.preview.derived", {
            fallback: "Derived — read-only, follow their bases",
          }),
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(118px,1fr))",
            gap: 6,
          }}
        >
          {DERIVED_VARS.map((k) => (
            <div
              key={k}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                border: "1px solid var(--br)",
                borderRadius: RAD.r5,
                padding: "3px 6px",
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: RAD.r4,
                  border: "1px solid var(--br)",
                  background: full[k],
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <span
                  style={{
                    fontSize: FS.s9_5,
                    color: "var(--tp)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {varLabel(k, t)}
                </span>
                <span
                  style={{
                    fontSize: FS.s8_5,
                    color: "var(--tm)",
                    fontFamily: "'JetBrains Mono',monospace",
                  }}
                >
                  {k}
                </span>
              </span>
            </div>
          ))}
        </div>

        {/* Zero-consumer editable token — labelled swatch, honestly marked */}
        {secLbl(
          t("theme.preview.nopresence", {
            fallback: "No live-UI presence (editable, currently unused)",
          }),
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            border: "1px dashed var(--br)",
            borderRadius: RAD.r5,
            padding: "3px 6px",
            width: "fit-content",
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: RAD.r4,
              border: "1px solid var(--br)",
              background: "var(--ct)",
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: FS.s9_5, color: "var(--tp)" }}>
            {varLabel("--ct", t)}
          </span>
          <span
            style={{
              fontSize: FS.s8_5,
              color: "var(--tm)",
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            {t("theme.preview.cap_ct_unused", {
              fallback: "--ct · 0 consumers",
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

// Audit summary panel. M+3.6.1: both tiers informational; red/amber
// preserved as severity hint, but neither blocks save.
function AuditPanel({ vars }: { vars?: any }) {
  const { t } = useTranslation();
  const audit = useMemo(() => auditContrast(vars || {}), [vars]);
  const renderRow = (r: any, kind: any) => {
    const fgIsDerived = OVERRIDABLE_DERIVED_VARS.includes(r.fg);
    return (
      <li
        key={`${r.fg}-${r.bg}-${kind}`}
        style={{
          fontSize: FS.s10_5,
          color: kind === "hard" ? "var(--rd)" : "var(--am)",
        }}
      >
        <strong>{varLabel(r.fg, t)}</strong>
        {" " + t("theme.audit.on", { fallback: "on" }) + " "}
        <strong>{varLabel(r.bg, t)}</strong>
        {` — ${r.ratio.toFixed(2)}:1 (${t("theme.audit.need_ratio", { fallback: "need 3.00:1" })})`}
        {fgIsDerived && (
          <span style={{ color: "var(--tm)", fontStyle: "italic" }}>
            {" "}
            —{" "}
            {t("theme.audit.derived_from_primary", {
              fallback: "derived from primary text; override or adjust source",
            })}
          </span>
        )}
      </li>
    );
  };
  if (audit.hardFails.length === 0 && audit.softWarnings.length === 0) {
    return (
      <div
        style={{
          marginTop: 8,
          fontSize: FS.s10_5,
          color: "var(--gn)",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Check size={12} />
        {t("theme.audit.all_pass", {
          fallback: "All contrast pairs pass AA-large (3:1).",
        })}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      {audit.hardFails.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div
            style={{
              fontSize: FS.s10_5,
              fontWeight: 600,
              color: "var(--rd)",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <AlertTriangle size={11} />
            {t("theme.audit.hard_count", {
              count: audit.hardFails.length,
              fallback:
                audit.hardFails.length +
                " text-contrast issue" +
                (audit.hardFails.length === 1 ? "" : "s") +
                ":",
            })}
          </div>
          <ul style={{ margin: "2px 0 0 18px", padding: 0 }}>
            {audit.hardFails.map((r) => renderRow(r, "hard"))}
          </ul>
        </div>
      )}
      {audit.softWarnings.length > 0 && (
        <div>
          <div style={{ fontSize: FS.s10_5, fontWeight: 600, color: "var(--am)" }}>
            {t("theme.audit.soft_count", {
              count: audit.softWarnings.length,
              fallback:
                audit.softWarnings.length +
                " soft warning" +
                (audit.softWarnings.length === 1 ? "" : "s") +
                " (status colours typically render on tinted bg):",
            })}
          </div>
          <ul style={{ margin: "2px 0 0 18px", padding: 0 }}>
            {audit.softWarnings.map((r) => renderRow(r, "soft"))}
          </ul>
        </div>
      )}
    </div>
  );
}

// 204 Part Three §2.2 — THE WINDOW-CHROME CARD, EXTRACTED (moved, not bettered):
// Phase 29 Stage-3 C22's bar colour + title (FirmSettings.windowBarColor /
// windowTitle — applied to the main document, mirrored into the pop-out by
// the sync effect, served via the dynamic PWA manifest). It carried its own
// state and effect inside ThemeManager's body, so it is a real component at
// module scope (Bug A's rule), never the function-call shortcut. Props are the
// three names the body read.
/** The three FirmSettings fields this card reads (typed at the seam, 181 A.3's rule). */
type WindowChromeSettings = {
  windowBarColor?: string | null;
  windowTitle?: string | null;
  firm?: { name?: string | null } | null;
};
export function WindowChromeCard({
  firmSettings,
  saveFirmSettings,
  toast,
}: Readonly<{
  firmSettings: WindowChromeSettings | null | undefined;
  /** App's `saveFirmSettings` — a labelled tuple, the house shape for a typed callback prop. */
  saveFirmSettings: (...args: [patch: { windowBarColor: string | null; windowTitle: string | null }]) => Promise<unknown>;
  toast: (...args: [msg: string]) => void;
}>) {
  const { t: tr } = useTranslation() as { t: (key: string, vars?: Record<string, unknown>) => string };
  // Phase 29 Stage-3 C22 — window-chrome overrides (bar color + title).
  const [winChrome, setWinChrome] = useState({ color: "", title: "" });
  useEffect(() => {
    setWinChrome({
      color: firmSettings?.windowBarColor ?? "",
      title: firmSettings?.windowTitle ?? "",
    });
  }, [firmSettings?.windowBarColor, firmSettings?.windowTitle]);
  const saveWinChrome = async () => {
    try {
      await saveFirmSettings({
        windowBarColor: winChrome.color || null,
        windowTitle: winChrome.title.trim() || null,
      });
      toast(
        tr("theme.window.saved", { fallback: "Window settings saved" }),
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "";
      toast("⚠ " + (msg || tr("theme.toast.save_failed", { fallback: "save failed" })));
    }
  };
  return (
  <div
    style={{
      background: "var(--bi)",
      borderRadius: RAD.r8,
      padding: 10,
      marginBottom: 10,
    }}
  >
    {/* 204 Part Three §2.2 — the card's own title (`theme.window.title`) and
        note (`theme.window.note`, the browser-limits paragraph) are gone: the
        section that mounts it carries ONE heading and ONE sentence [RULED].
        Their rows stay seeded and unread. */}
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>
          {tr("theme.window.barColor", { fallback: "Window bar color" })}
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <input
            type="color"
            value={winChrome.color || CT.c_4A7CFF}
            onChange={(e) =>
              setWinChrome((p) => ({ ...p, color: e.target.value }))
            }
            style={{
              width: 34,
              height: 26,
              padding: 0,
              border: "1px solid var(--br)",
              borderRadius: RAD.r6,
              background: "var(--bi)",
              cursor: "pointer",
            }}
          />
          <button
            type="button"
            className="btn"
            style={{ padding: "3px 8px", fontSize: FS.s10 }}
            disabled={!winChrome.color}
            onClick={() => setWinChrome((p) => ({ ...p, color: "" }))}
            title={tr("theme.window.useTheme_tip", {
              fallback: "No override — follow the active theme",
            })}
          >
            {tr("theme.window.useTheme", { fallback: "Theme color" })}
          </button>
        </div>
      </div>
      <div className="field" style={{ margin: 0, flex: "1 1 160px" }}>
        <label>
          {tr("theme.window.titleText", { fallback: "Window title" })}
        </label>
        <input
          className="field-input"
          style={{ padding: "4px 8px", fontSize: FS.s11 }}
          placeholder={
            firmSettings?.firm?.name || "Legal Practice Manager"
          }
          value={winChrome.title}
          onChange={(e) =>
            setWinChrome((p) => ({ ...p, title: e.target.value }))
          }
        />
      </div>
      <button
        type="button"
        className="btn btn-primary"
        style={{ padding: "4px 12px", fontSize: FS.s11 }}
        disabled={
          (winChrome.color || "") ===
            (firmSettings?.windowBarColor || "") &&
          (winChrome.title.trim() || "") ===
            (firmSettings?.windowTitle || "")
        }
        onClick={saveWinChrome}
      >
        {tr("common.save", { fallback: "Save" })}
      </button>
    </div>
  </div>
  );
}

// M+3.6 C15 — auto-night-switch reverts to per-user. Controls live in
// Settings → My display, not in the admin Theme Manager.

export default function ThemeManager({
  themes,
  setThemes,
  firmSettings,
  saveFirmSettings,
  toast,
  // Phase 44 — two-tier exposure. `admin` gates the firm-branding sections
  // (slot pinning / push / window chrome); non-admins get the colour-editing
  // subset. viewerId + isBasis/createdById drive per-card edit/delete. onUseTheme
  // + selectedThemeId are the selection view shared with the sidebar Personal slot.
  admin = false,
  viewerId = null,
  selectedThemeId = null,
  onUseTheme,
}: any) {
  const { t: tr } = useTranslation();
  const [editingId, setEditingId] = useState<any>(null);
  const [editBuffer, setEditBuffer] = useState<any>(null); // { name, vars }
  const [creatingName, setCreatingName] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const importRef = useRef<any>(null);
  const [pushDate, setPushDate] = useState("");

  useEffect(() => {
    setPushDate(
      firmSettings?.customPushUntil
        ? isoToBxlDateInput(firmSettings.customPushUntil)
        : "",
    );
  }, [firmSettings?.customPushUntil]);
  const editingTheme = themes.find((t: any) => t.id === editingId);

  const startEdit = (t: any) => {
    const seedVars: Record<string, any> = {};
    for (const k of EDITABLE_VARS)
      seedVars[k] = (t.vars && t.vars[k]) || varDefault(k);
    // Carry forward any override-able derived vars stored on the theme so
    // the editor surfaces them as overridden, not as fresh derivation.
    for (const k of OVERRIDABLE_DERIVED_VARS) {
      if (t.vars && Object.prototype.hasOwnProperty.call(t.vars, k)) {
        seedVars[k] = t.vars[k];
      }
    }
    setEditBuffer({ name: t.name, vars: seedVars });
    setEditingId(t.id);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditBuffer(null);
  };

  const saveEdit = async () => {
    if (!editingTheme || !editBuffer) return;
    try {
      const updated = (await api.patch(`/themes/${editingTheme.id}`, {
        name: editBuffer.name,
        vars: editBuffer.vars,
      })) as { id: number; name: string };
      setThemes((prev: any) => prev.map((t: any) => (t.id === updated.id ? updated : t)));
      toast(tr("theme.toast.saved", { fallback: "Theme saved" }));
      cancelEdit();
    } catch (e: any) {
      toast("⚠ " + (e.message || tr("theme.toast.save_failed", { fallback: "save failed" })));
    }
  };

  const createTheme = async () => {
    const name = creatingName.trim();
    if (!name) {
      toast(tr("theme.toast.name_required", { fallback: "Name required" }));
      return;
    }
    const seed = themes[0]; // start from existing theme so vars are valid
    const vars: Record<string, any> = {};
    for (const k of EDITABLE_VARS)
      vars[k] = (seed?.vars && seed.vars[k]) || varDefault(k);
    try {
      const row = (await api.post("/themes", { name, vars })) as { id: number; name: string };
      setThemes((prev: any) => [...prev, row]);
      setCreatingName("");
      setShowCreate(false);
      toast(
        tr("theme.toast.created", {
          name: row.name,
          fallback: `Theme “${row.name}” created`,
        }),
      );
      startEdit(row);
    } catch (e: any) {
      toast("⚠ " + (e.message || tr("theme.toast.create_failed", { fallback: "create failed" })));
    }
  };

  const deleteTheme = async (t: any) => {
    try {
      await api.del(`/themes/${t.id}`);
      setThemes((prev: any) => prev.filter((x: any) => x.id !== t.id));
      toast(
        tr("theme.toast.deleted", {
          name: t.name,
          fallback: `Theme “${t.name}” deleted`,
        }),
      );
    } catch (e: any) {
      toast("⚠ " + (e.message || tr("theme.toast.delete_failed", { fallback: "delete failed" })));
    }
  };

  // Phase 44 — export a theme to a shareable file carrying {name, vars} only
  // (no id/createdById/tier/slots travel).
  const exportTheme = (t: any) => {
    const payload = JSON.stringify({ name: t.name, vars: t.vars || {} }, null, 2);
    const blob = new Blob([payload], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // 181 (ruling 1) — a space for what a name may not carry, not '_'.
    a.download = `${String(t.name || "theme").replace(/[^a-z0-9._-]+/gi, " ").trim()}.theme.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Import lands as a PERSONAL theme owned by the importer (isBasis:false); the
  // server sanitizes vars (hex-only allowlist) and auto-numbers the name.
  const importThemeFile = async (e: any) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const name = typeof parsed?.name === "string" ? parsed.name.trim() : "";
      const vars =
        parsed && typeof parsed.vars === "object" ? parsed.vars : null;
      if (!name || !vars) {
        toast(
          "⚠ " +
            tr("theme.toast.not_a_theme_file", {
              fallback: "Not a theme file (needs {name, vars})",
            }),
        );
        return;
      }
      const row = (await api.post("/themes", { name, vars, isBasis: false })) as { id: number; name: string };
      setThemes((prev: any) => [...prev, row]);
      toast(
        tr("theme.toast.imported", {
          name: row.name,
          fallback: `Imported “${row.name}”`,
        }),
      );
    } catch (err: any) {
      toast("⚠ " + (err.message || tr("theme.toast.import_failed", { fallback: "import failed" })));
    }
  };

  const pinSlot = async (slot: any, themeId: any) => {
    const field =
      slot === "day"
        ? "dayThemeId"
        : slot === "night"
          ? "nightThemeId"
          : "customThemeId";
    try {
      await saveFirmSettings({ [field]: themeId });
      const slotName = tr(
        slot === "day"
          ? "theme.slot.day"
          : slot === "night"
            ? "theme.slot.night"
            : "theme.custom_default_name",
        { fallback: slot === "day" ? "Day" : slot === "night" ? "Night" : "Custom" },
      );
      toast(
        tr("theme.toast.slot_updated", {
          slot: slotName,
          fallback: `${slotName} default updated`,
        }),
      );
    } catch (e: any) {
      toast("⚠ " + (e.message || tr("theme.toast.slot_save_failed", { fallback: "slot save failed" })));
    }
  };

  const savePush = async () => {
    const iso = pushDate ? endOfDayBrusselsISO(pushDate) : null;
    try {
      await saveFirmSettings({ customPushUntil: iso });
      toast(
        iso
          ? tr("theme.toast.push_set_until", {
              date: fmtBxlDate(iso),
              fallback: `Push set until ${fmtBxlDate(iso)}`,
            })
          : tr("theme.toast.push_cleared", { fallback: "Push cleared" }),
      );
    } catch (e: any) {
      toast("⚠ " + (e.message || tr("theme.toast.push_save_failed", { fallback: "push save failed" })));
    }
  };

  const cancelPush = async () => {
    try {
      await saveFirmSettings({ customPushUntil: null });
      setPushDate("");
      toast(tr("theme.toast.push_cancelled", { fallback: "Push cancelled" }));
    } catch (e: any) {
      toast("⚠ " + (e.message || tr("theme.toast.cancel_failed", { fallback: "cancel failed" })));
    }
  };

  const slotFor = (themeId: any) => {
    const slots = [];
    if (firmSettings?.dayThemeId === themeId) slots.push("day");
    if (firmSettings?.nightThemeId === themeId) slots.push("night");
    if (firmSettings?.customThemeId === themeId) slots.push("custom");
    return slots;
  };

  const pushActive =
    firmSettings?.customPushUntil &&
    new Date(firmSettings.customPushUntil) > new Date();
  const customTheme = themes.find((t: any) => t.id === firmSettings?.customThemeId);
  const customThemeName =
    customTheme?.name ||
    tr("theme.custom_default_name", { fallback: "Custom" });

  return (
    <div>
      {admin && (
        <>
      {/* Slot pinning — firm-branding, admin only (Phase 44 D2) */}
      <div
        style={{
          background: "var(--bi)",
          borderRadius: RAD.r8,
          padding: 10,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: FS.s11,
            fontWeight: 600,
            color: "var(--tp)",
            marginBottom: 6,
          }}
        >
          {tr("theme.slot_defaults", { fallback: "Slot defaults" })}
        </div>
        {["day", "night", "custom"].map((slot) => {
          const slotField =
            slot === "day"
              ? "dayThemeId"
              : slot === "night"
                ? "nightThemeId"
                : "customThemeId";
          const current = firmSettings?.[slotField];
          const slotLbl =
            slot === "day"
              ? tr("theme.slot.day_default", { fallback: "Day default" })
              : slot === "night"
                ? tr("theme.slot.night_default", { fallback: "Night default" })
                : tr("theme.slot.custom_default", {
                    fallback: "Custom default",
                  });
          return (
            <div
              key={slot}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 0",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: FS.s10_5,
                  color: "var(--ts)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 110,
                }}
              >
                <SlotIcon slot={slot} />
                {slotLbl}
              </span>
              <MenuSelect
                className="field-input"
                style={{
                  flex: 1,
                  minWidth: 160,
                  padding: "3px 6px",
                  fontSize: FS.s11,
                }}
                value={current != null ? String(current) : ""}
                onChange={(v) => pinSlot(slot, Number(v))}
                options={themes.map((th: any) => [String(th.id), String(th.name)] as const)}
              />
            </div>
          );
        })}
      </div>

      {/* Custom push */}
      <div
        style={{
          background: "var(--bi)",
          borderRadius: RAD.r8,
          padding: 10,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: FS.s11,
            fontWeight: 600,
            color: "var(--tp)",
            marginBottom: 4,
          }}
        >
          {tr("theme.push.title", { fallback: "Push custom theme firm-wide" })}
        </div>
        <p
          style={{
            fontSize: FS.s10_5,
            color: "var(--tm)",
            margin: "0 0 8px",
            lineHeight: 1.5,
          }}
        >
          {tr("theme.push.desc", {
            fallback:
              "While active, every user is forced onto the Custom default. Users can still toggle to Day/Night for their session.",
          })}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <label style={{ fontSize: FS.s10_5, color: "var(--ts)" }}>
            {tr("theme.push.active_until", { fallback: "Active until end of" })}
          </label>
          <input
            type="date"
            className="field-input"
            style={{ padding: "3px 6px", fontSize: FS.s11 }}
            value={pushDate}
            onChange={(e) => setPushDate(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary"
            style={{ padding: "3px 10px", fontSize: FS.s11 }}
            onClick={savePush}
            disabled={
              !pushDate ||
              pushDate === isoToBxlDateInput(firmSettings?.customPushUntil)
            }
          >
            {tr("theme.push.save", { fallback: "Save push" })}
          </button>
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: FS.s10_5,
            color: pushActive ? "var(--am)" : "var(--tm)",
          }}
        >
          {pushActive ? (
            <>
              <strong>{customThemeName}</strong>{" "}
              {tr("theme.push.active_until_label", {
                fallback: "active until",
              })}{" "}
              <strong>{fmtBxlDate(firmSettings.customPushUntil)}</strong>.{" "}
              <button
                type="button"
                onClick={cancelPush}
                style={{
                  marginLeft: 6,
                  fontSize: FS.s10,
                  padding: "1px 6px",
                  background: "none",
                  border: "1px solid var(--br)",
                  color: "var(--ts)",
                  cursor: "pointer",
                  borderRadius: RAD.r4,
                }}
              >
                {tr("theme.push.cancel", { fallback: "Cancel push" })}
              </button>
            </>
          ) : (
            tr("theme.push.no_active", { fallback: "No active push." })
          )}
        </div>
      </div>

      {/* Phase 29 Stage-3 C22's window-chrome card (bar colour + title) is
          `WindowChromeCard` below and is MOUNTED BY SettingsView since 204
          Part Three §2.2, under the firm's identity beside the personal
          « Browser bar colour » — not here. */}
        </>
      )}

      {/* Theme library — colour-editing subset, renders for all users */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: FS.s11, fontWeight: 600, color: "var(--tp)" }}>
          {tr("theme.library", { fallback: "Theme library" })}
        </span>
        <button
          type="button"
          className="btn btn-primary"
          style={{
            padding: "3px 10px",
            fontSize: FS.s11,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
          onClick={() => setShowCreate((s) => !s)}
        >
          <Plus size={11} />
          {admin
            ? tr("theme.new_theme", { fallback: "New theme" })
            : tr("theme.new_personal_theme", {
                fallback: "New personal theme",
              })}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          style={{
            padding: "3px 10px",
            fontSize: FS.s11,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
          onClick={() => importRef.current && importRef.current.click()}
          title={tr("theme.import_tip", {
            fallback: "Import a theme file (lands as a personal theme)",
          })}
        >
          <Upload size={11} />
          {tr("theme.import", { fallback: "Import" })}
        </button>
        <input
          ref={importRef}
          type="file"
          accept="application/json,.json"
          style={{ display: "none" }}
          onChange={importThemeFile}
        />
      </div>

      {/* Phase 44 — legibility: frame personal-theme authoring for non-admins. */}
      <p
        style={{
          fontSize: FS.s10_5,
          color: "var(--tm)",
          margin: "0 0 8px",
          lineHeight: 1.5,
        }}
      >
        {admin
          ? tr("theme.library_blurb_admin", {
              fallback:
                "Firm themes (pinnable to the Day/Night/Custom slots) plus everyone’s personal themes. “New theme” creates a firm theme.",
            })
          : tr("theme.library_blurb_user", {
              fallback:
                "Design your own colour theme with “New personal theme” — it’s yours to wear via the Personal slot in the sidebar, and colleagues can pick it too. Firm themes below are set by an admin.",
            })}
      </p>

      {showCreate && (
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 8,
            alignItems: "center",
          }}
        >
          <input autoComplete="off" /* 207 §18 */
            className="field-input no-contact-autofill"
            placeholder={tr("theme.name_placeholder", {
              fallback: "Theme name",
            })}
            value={creatingName}
            style={{ flex: 1, padding: "3px 6px", fontSize: FS.s11 }}
            onChange={(e) => setCreatingName(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="btn btn-primary"
            style={{ padding: "3px 10px", fontSize: FS.s11 }}
            onClick={createTheme}
          >
            {tr("common.create", { fallback: "Create" })}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: "3px 10px", fontSize: FS.s11 }}
            onClick={() => {
              setShowCreate(false);
              setCreatingName("");
            }}
          >
            {tr("common.cancel", { fallback: "Cancel" })}
          </button>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
          gap: 8,
        }}
      >
        {themes.map((t: any) => {
          const slots = slotFor(t.id);
          const isEditing = editingId === t.id;
          const showVars = isEditing ? editBuffer.vars : t.vars;
          /* 135 §2(a) — what the theme RESOLVES TO, for the pickers to fall
             back on. `--brand` is stored only once a firm has chosen one, and
             until then its value is this theme's accent — a fact `varDefault`'s
             static map cannot carry. Computed here rather than inside the
             override section's IIFE (which builds its own `derivedFull` from
             the same call) because the base-token grid above it needs it too;
             the duplication is one line and the alternative is threading a
             value through two nested blocks. */
          const derivedNow = deriveThemeVars(showVars || {});
          return (
            <div
              key={t.id}
              style={{
                border: "1px solid var(--br)",
                borderRadius: RAD.r8,
                padding: 10,
                background: "var(--bc)",
                // Phase 45 — while editing, span the full library-grid width so
                // the editor can lay out as two columns (pickers | sticky
                // preview) instead of one tall ~1/3-width column.
                gridColumn: isEditing ? "1 / -1" : "auto",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {isEditing ? (
                  <input
                    className="field-input"
                    value={editBuffer.name}
                    onChange={(e) =>
                      setEditBuffer((b: any) => ({ ...b, name: e.target.value }))
                    }
                    style={{
                      flex: 1,
                      padding: "2px 6px",
                      fontSize: FS.s12,
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <span
                    style={{
                      flex: 1,
                      fontSize: FS.s12,
                      fontWeight: 600,
                      color: "var(--tp)",
                    }}
                  >
                    {t.name}
                  </span>
                )}
                {(() => {
                  const mine =
                    t.isBasis === false && t.createdById === viewerId;
                  const label = t.isBasis
                    ? tr("theme.tier.firm", { fallback: "Firm" })
                    : mine
                      ? tr("theme.tier.mine", { fallback: "Personal · yours" })
                      : tr("theme.tier.shared", { fallback: "Personal" });
                  return (
                    <span
                      style={{
                        fontSize: FS.s8_5,
                        fontWeight: 600,
                        color: mine ? "var(--gn)" : "var(--tm)",
                        border: "1px solid var(--br)",
                        borderRadius: RAD.r8,
                        padding: "1px 5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </span>
                  );
                })()}
                {admin &&
                  slots.map((s) => (
                    <span
                      key={s}
                      title={tr("theme.pinned_as", {
                        slot: s,
                        fallback: "Pinned as " + s,
                      })}
                      style={{ color: "var(--ac)" }}
                    >
                      <SlotIcon slot={s} size={12} />
                    </span>
                  ))}
                {!isEditing && (
                  <>
                    {t.id === selectedThemeId ? (
                      <span
                        title={tr("theme.in_use_tip", {
                          fallback: "This theme is currently applied",
                        })}
                        style={{
                          fontSize: FS.s9,
                          fontWeight: 700,
                          color: "var(--gn)",
                          padding: "2px 6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tr("theme.in_use", { fallback: "In use" })}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: "2px 6px", fontSize: FS.s10 }}
                        onClick={() => onUseTheme && onUseTheme(t.id)}
                        title={tr("theme.use_tip", {
                          fallback: "Use this theme",
                        })}
                      >
                        <Check size={11} />
                      </button>
                    )}
                    {(admin ||
                      (t.isBasis === false &&
                        t.createdById === viewerId)) && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: "2px 6px", fontSize: FS.s10 }}
                        onClick={() => startEdit(t)}
                        title={tr("theme.edit_tip", { fallback: "Edit theme" })}
                      >
                        <Pencil size={11} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: "2px 6px", fontSize: FS.s10 }}
                      onClick={() => exportTheme(t)}
                      title={tr("theme.export_tip", {
                        fallback: "Export theme to a file",
                      })}
                    >
                      <Download size={11} />
                    </button>
                    {(admin ||
                      (t.isBasis === false &&
                        t.createdById === viewerId)) && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ padding: "2px 6px", fontSize: FS.s10 }}
                        onClick={() => deleteTheme(t)}
                        title={tr("theme.delete_tip", {
                          fallback: "Delete theme",
                        })}
                        disabled={slots.length > 0}
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </>
                )}
              </div>

              {isEditing ? (
                <>
                  {/* Phase 45 — two-column editor: pickers left, live scoped
                      preview right (sticky). flex-wrap stacks them back into
                      one column on viewports too narrow for two. */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 16,
                      alignItems: "flex-start",
                      marginTop: 8,
                    }}
                  >
                    <div style={{ flex: "1 1 420px", minWidth: 0 }}>
                  {varGroups().map((g) => (
                    <div key={g.heading} style={{ marginTop: 8 }}>
                      <div
                        style={{
                          fontSize: FS.s10,
                          fontWeight: 600,
                          color: "var(--tm)",
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          marginBottom: 4,
                        }}
                      >
                        {g.heading}
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit,minmax(180px,1fr))",
                          gap: 8,
                        }}
                      >
                        {g.vars.map((k) => (
                          <ColorOrVarPicker
                            key={k}
                            /* 135 §2(a)/§4 — the label carries the var's
                               sentence where it has one. `varSource` returns
                               "" for a var without, so every picker asks and
                               only `--brand` answers today; the label prop is a
                               ReactNode, so this needs no new prop and no
                               group of one to hang a paragraph off. It is the
                               sentence someone CHOOSING the colour reads, which
                               is why it says what the colour does to the app
                               rather than how the derivation works. */
                            label={
                              varSource(k, tr) ? (
                                <>
                                  {varLabel(k, tr)}
                                  <span
                                    style={{
                                      display: "block",
                                      fontSize: FS.s9,
                                      lineHeight: 1.45,
                                      color: "var(--tm)",
                                      marginTop: 2,
                                    }}
                                  >
                                    {varSource(k, tr)}
                                  </span>
                                </>
                              ) : (
                                varLabel(k, tr)
                              )
                            }
                            /* 135 §2(a) — THE DERIVED VALUE COMES SECOND, AND
                               BEFORE `varDefault`. `--brand` defaults to the
                               THEME'S accent, and `varDefault` is a static map
                               that cannot know it: a firm that had re-themed
                               would have opened this picker on the stock blue
                               and saved a colour it never chose. `derivedNow`
                               is `deriveThemeVars` over the same buffer the
                               preview uses, so the picker opens on exactly what
                               the screen is already showing. Every other var in
                               these groups is stored outright, so for them this
                               arm never fires. */
                            value={showVars[k] || derivedNow[k] || varDefault(k)}
                            onChange={(v) =>
                              setEditBuffer((b: any) => ({
                                ...b,
                                vars: { ...b.vars, [k]: v },
                              }))
                            }
                            mode="hex-only"
                            swatchSize={32}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  {(() => {
                    // Overrides for the two derivable text vars. Toggle the
                    // override on to pin a specific value; toggle off to
                    // re-derive from --tp / --bs at the next render.
                    const derivedFull = deriveThemeVars(editBuffer.vars);
                    return (
                      <div style={{ marginTop: 8 }}>
                        <div
                          style={{
                            fontSize: FS.s10,
                            fontWeight: 600,
                            color: "var(--tm)",
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                            marginBottom: 4,
                          }}
                        >
                          {/* 111 §5d — the two highlight tokens joined this
                              group, so the heading/desc that said "text" would
                              state something false; NEW keys per the 105
                              insert-only rule (the old rows stay seeded and
                              unread, D28). */}
                          {tr("theme.overrides.title_all", {
                            fallback: "Derived overrides",
                          })}
                        </div>
                        <p
                          style={{
                            fontSize: FS.s10,
                            color: "var(--tm)",
                            margin: "0 0 6px",
                            lineHeight: 1.5,
                          }}
                        >
                          {/* 134 §4(a) — IT STOPS ENUMERATING, and that is the
                              fix rather than an addition to the list. The
                              sentence named four of five sources and could
                              never name the fifth: `--nwd` joined this group in
                              132 §7 and nobody rewrote it, so the "Non-working
                              day field" row explained everything except itself.
                              A shared sentence that lists its members is a
                              census with an expiry date (130-A), so each row
                              carries its OWN source now and this one says what
                              the section is and how the box works.
                              NEW KEY, per the 105 insert-only rule — the seed
                              only INSERTS, so editing `desc_all` would leave
                              every seeded instance reading the old list. */}
                          {tr("theme.overrides.desc_rows", {
                            fallback:
                              "Each row below is computed from other tokens in this theme — its own source is named under its name. Tick a row to pin a colour of your own; untick it to hand it back to the derivation.",
                          })}
                        </p>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit,minmax(220px,1fr))",
                            gap: 8,
                          }}
                        >
                          {OVERRIDABLE_DERIVED_VARS.map((k) => {
                            const isOverridden =
                              Object.prototype.hasOwnProperty.call(
                                editBuffer.vars,
                                k,
                              );
                            const computed = derivedFull[k];
                            return (
                              <div
                                key={k}
                                style={{
                                  border: "1px solid var(--br)",
                                  borderRadius: RAD.r6,
                                  padding: 6,
                                }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    fontSize: FS.s10_5,
                                    color: "var(--ts)",
                                    marginBottom: 4,
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isOverridden}
                                    onChange={(e) =>
                                      setEditBuffer((b: any) => {
                                        const next = { ...b.vars };
                                        if (e.target.checked) {
                                          next[k] = computed;
                                        } else {
                                          delete next[k];
                                        }
                                        return { ...b, vars: next };
                                      })
                                    }
                                  />
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "var(--tp)",
                                    }}
                                  >
                                    {varLabel(k, tr)}
                                  </span>
                                  {/* 134 §4(c) — THE BOX'S TWO STATES ARE
                                      NAMED. Unticking already returns the row
                                      to its derivation — that is the "revert"
                                      Laurent asked for, and it has always
                                      worked — but NOTHING ON SCREEN SAID SO,
                                      so the only way to learn it was to try it.
                                      One word each, beside the box: what this
                                      row is now, and therefore what the other
                                      position means. */}
                                  <span
                                    style={{
                                      marginLeft: "auto",
                                      fontSize: FS.s9,
                                      color: isOverridden
                                        ? "var(--ac)"
                                        : "var(--tm)",
                                      fontWeight: 600,
                                      textTransform: "uppercase",
                                      letterSpacing: 0.3,
                                      whiteSpace: "nowrap",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {isOverridden
                                      ? tr("theme.overrides.state.on", {
                                          fallback: "Overridden",
                                        })
                                      : tr("theme.overrides.state.off", {
                                          fallback: "Derived",
                                        })}
                                  </span>
                                  {/* `nowrap`, because the row now carries a
                                      third item: "Non-working day field" plus
                                      OVERRIDDEN plus `--nwd` in a 220px card
                                      broke the token across two lines as "--" /
                                      "nwd", and a CSS variable split in half is
                                      not a name. */}
                                  <span
                                    style={{
                                      fontSize: FS.s9,
                                      color: "var(--tm)",
                                      fontFamily: "'JetBrains Mono',monospace",
                                      whiteSpace: "nowrap",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {k}
                                  </span>
                                </label>
                                {/* 134 §4(a) — THE SOURCE, ON THE ROW, read out
                                    of `themeDerivation.ts` where the derivation
                                    itself lives. It says what MOVES if the base
                                    moves, because that following is exactly
                                    what ticking the box gives up. */}
                                {varSource(k, tr) && (
                                  <p
                                    style={{
                                      margin: "0 0 5px",
                                      fontSize: FS.s9,
                                      lineHeight: 1.45,
                                      color: "var(--tm)",
                                    }}
                                  >
                                    {varSource(k, tr)}
                                  </p>
                                )}
                                {isOverridden ? (
                                  <ColorOrVarPicker
                                    value={editBuffer.vars[k] || computed}
                                    onChange={(v) =>
                                      setEditBuffer((b: any) => ({
                                        ...b,
                                        vars: { ...b.vars, [k]: v },
                                      }))
                                    }
                                    mode="hex-only"
                                    swatchSize={28}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      fontSize: FS.s10,
                                      color: "var(--tm)",
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 28,
                                        height: 28,
                                        borderRadius: RAD.r5,
                                        border: "1px solid var(--br)",
                                        background: computed,
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontFamily:
                                          "'JetBrains Mono',monospace",
                                      }}
                                    >
                                      {computed}
                                    </span>
                                    {/* 134 §4(c) — the italic "derived" that
                                        stood here is GONE, not moved: the state
                                        word now sits beside the checkbox, where
                                        (c) asks for it, and two of the same
                                        word in one small card is one too many.
                                        The `theme.overrides.derived` rows stay
                                        seeded and unread (D28, insert-only). */}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                    </div>
                    {/* RIGHT — live scoped preview + soft-warning audit, made
                        sticky so every picker's effect stays visible while the
                        left column scrolls; maxHeight+auto lets a tall preview
                        self-scroll rather than slide off-screen. */}
                    <div
                      style={{
                        flex: "1 1 300px",
                        minWidth: 0,
                        position: "sticky",
                        top: 8,
                        alignSelf: "flex-start",
                        maxHeight: "calc(100vh - 92px)",
                        overflowY: "auto",
                      }}
                    >
                      <ScopedThemePreview vars={editBuffer.vars} />
                      <AuditPanel vars={editBuffer.vars} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ padding: "4px 12px", fontSize: FS.s11 }}
                      onClick={saveEdit}
                    >
                      {tr("common.save", { fallback: "Save" })}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: "4px 12px", fontSize: FS.s11 }}
                      onClick={cancelEdit}
                    >
                      {tr("common.cancel", { fallback: "Cancel" })}
                    </button>
                  </div>
                </>
              ) : (
                <LivePreview vars={t.vars} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
